import {Material} from '@domain/materials'
import {InfoOutlined} from '@mui/icons-material'
import {Box, Tooltip} from '@mui/material'
import React from 'react'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {Field} from '../../types'

import {ConfigControlledField} from './ConfigControlledField'
import {shouldDisableProductionDayTimeField} from './utils/productionDayUtils'

interface ProductionDayTimeFieldsProps {
  field: Field
  endTimeField: Field
  materials: Material[]
  upmId: string
}

export const ProductionDayTimeFields: React.FC<ProductionDayTimeFieldsProps> = ({
  field,
  endTimeField,
  materials,
  upmId
}) => {
  const {t} = useTranslation()
  const {control, watch, clearErrors} = useFormContext()

  const watchUseCustomProductionDayBoundaries = watch('useCustomProductionDayBoundaries')
  const watchMothballed = watch('mothballed')
  const startDate = watch('commissStartDate')
  const endDate = watch('commissEndDate')

  const startTimeDisabled = shouldDisableProductionDayTimeField(
    'productionDayStartTime',
    watchUseCustomProductionDayBoundaries as string
  )
  const endTimeDisabled = shouldDisableProductionDayTimeField(
    'productionDayEndTime',
    watchUseCustomProductionDayBoundaries as string
  )

  const ignoreValidation = watchMothballed === 'true'

  return (
    <Box display="flex" gap={2}>
      <Box flex={1}>
        <ConfigControlledField
          upmId={upmId}
          clearErrors={clearErrors}
          disabled={startTimeDisabled}
          control={control}
          field={field}
          materials={materials}
          startDate={startDate as string}
          endDate={endDate as string}
          ignoreValidation={ignoreValidation}
          isOptional={field.optional || ignoreValidation}
        />
      </Box>
      <Box flex={1} position="relative">
        <Tooltip title={t('plantSetup.productionDayEndTimeTooltip')}>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: '12px',
              right: '0px',
              display: 'inline-flex',
              cursor: 'pointer',
              zIndex: 1
            }}
          >
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: 'rgba(0, 0, 0, 0.54)',
                '&:hover': {
                  color: 'rgba(0, 0, 0, 0.87)'
                }
              }}
            />
          </Box>
        </Tooltip>
        <ConfigControlledField
          upmId={upmId}
          clearErrors={clearErrors}
          disabled={endTimeDisabled}
          control={control}
          field={endTimeField}
          materials={materials}
          startDate={startDate as string}
          endDate={endDate as string}
          ignoreValidation={ignoreValidation}
          isOptional={endTimeField.optional || ignoreValidation}
        />
      </Box>
    </Box>
  )
}
